import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";

import EditIcon from '@mui/icons-material/Edit';
import { Delete, TryOutlined } from "@mui/icons-material";

import { TableRow, TableCell, IconButton, Box, Button, DialogActions, DialogContentText, Dialog, DialogTitle, DialogContent, Collapse, Typography, Table, TableHead, TableBody, useTheme } from "@mui/material";
import DialogModifyMensualidad from "./DialogModifyMensualidad";
import { green, grey, orange, red } from "@mui/material/colors";
import DialogCancelMensualidad from "./DialogCancelMensualidad";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';

import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';
import DialogRenovarModify from "./DialogRenovarMensuaidad";
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

import ListAltIcon from '@mui/icons-material/ListAlt';

import TicketEntradaForm from "./TicketEntradaForm";
import TicketRenovacionFormOptimizado from "./TicketRenovacionFormOptimizado";
import { closePdfPopup, openPdfBlob, openPdfPopup } from "../../../utils/api";
import { getCurrentMensualidad } from "../../../utils/mensualidadFilters";
import { getHighlightScrollBehavior } from "../../../utils/createdRecord";
import { getPersistentRowHighlightSx } from "../../../utils/rowHighlights";
import { authenticatedSessionRequestConfig } from "../../../utils/csrf";
import { annulMonthlyTicket } from "../../../utils/monthlyTicketCommands";
import { formatMonthlyTicketObservations } from "../../../utils/monthlyTicketObservations";

const API_URL = process.env.REACT_APP_API_URL;

const formatNumber = (num) => {
  const number = Number(num);

  if (isNaN(number)) {
    console.warn("Valor inválido para formatNumber:", num);
    return "0,00"; // Valor por defecto si no es un número
  }

  const [integer, decimal] = number.toFixed(2).split(".");
  const integerFormatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${integerFormatted},${decimal}`;
};

// Hook personalizado para mantener el estado interno de tickets basado en props.
// Esto evita que el componente se re-renderice innecesariamente cuando la lista
// padre se mantiene igual, y permite anular tickets localmente.
function useTickets(ticketsArray) {
  const [list, setList] = useState([]);

  useEffect(() => {
    setList(Array.isArray(ticketsArray) ? ticketsArray : []);
  }, [ticketsArray]);

  return [list, setList];
}

const RowTableMensualidad = React.memo(({d, handleDelete, handleUpdateData, areUserAdmin, highlighted, scrollHighlight, onHighlightLocated, searchHighlighted, highlightSource}) => {
  const rowRef = React.useRef(null);
  const hasScrolledRef = React.useRef(false);
  const theme = useTheme();
  useEffect(() => {
    if (!highlighted || !scrollHighlight || hasScrolledRef.current) return;
    hasScrolledRef.current = true;
    rowRef.current?.scrollIntoView({ behavior: getHighlightScrollBehavior(), block: 'center' });
    onHighlightLocated?.(d.id);
  }, [d.id, highlighted, onHighlightLocated, scrollHighlight]);
  const [data, setData] = useState(d);
  useEffect(() => {
    setData(d);
  }, [d]);

  const {
    fechaInicio,
    fechaFinal,
    valor,
    observaciones,
    estado,
    faltanTexto,
    colorFondo,
  } = useMemo(() => {
    const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Bogota' });
    const current = getCurrentMensualidad(data, today);
    let valor = data.valor;
    let observaciones = data.observaciones;

    if (current.renovacion) {
      const ultimaRenovacion = current.renovacion;
      observaciones = ultimaRenovacion.observaciones;
      valor = ultimaRenovacion.valor;
    }

    const colorFondo = {
      cancelada: grey[500],
      renovada: orange[700],
      esperando: orange[700],
      termina_hoy: green[700],
      vencida: red[700],
    }[current.visualCategory] || grey[500];

    return {
      fechaInicio: current.fechaInicio,
      fechaFinal: current.fechaFinal,
      valor,
      observaciones,
      estado: current.estado,
      faltanTexto: current.faltanTexto,
      colorFondo,
    };
  }, [data]);

  const [openAlertDelete, setOpenAlertDelete] = useState(false);
  const [openModifyDialog, setOpenModifyDialog] = useState(false);

  const [openDialogRenovar, setOpenDialogRenovar] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const [openDialogPrint, setOpenDialogPrint] = useState(false);

  const [openHistorialDialog, setOpenHistorialDialog] = useState(false);

  const [openTicketEntradaForm, setOpenTicketEntradaForm] = useState(false);
  const [openTicketRenovacionForm, setOpenTicketRenovacionForm] = useState(false);
  const [openTicketAnular, setOpenTicketAnular] = useState(false);
  
  const [openTicketsHistorial, setOpenTicketsHistorial] = useState(false);

  const handleOpenDialogPrint = useCallback(() => {
    setOpenDialogPrint(true);
  }, []);

  const handleCloseDialogPrint = useCallback(() => {
    setOpenDialogPrint(false);
  }, []);
  
  const handleOpenModifyDialog = useCallback(() => {
    setOpenModifyDialog(true);
  }, []);

  const handleCloseModifyDialog = useCallback(() => {
    setOpenModifyDialog(false);
  }, []);

  const handleCloseCancelDialog = useCallback(() => {
    setOpenCancelDialog(false);
  }, []);

  const handleCloseRenovarDialog = useCallback(() => {
    setOpenDialogRenovar(false);
  }, []);

  const estaAtrasadoYNoSoyAdmin = estado === 'ATRASADO' ? (areUserAdmin === true ? true : false) : true

  const renovacionesConPrimerElementoMensualidad = React.useMemo(() => {
    const base = {
      id: data.id,
      fecha_entrada: data.fecha_inicio,
      fecha_salida: data.fecha_final,
      valor: data.valor,
      observaciones: data.observaciones,
      usuario: data.usuario.nombre,
    };
    return [base, ...(data.renovaciones || [])];
  }, [data]);

  const actualizarValorDeUnaRenovacion = (id, valor) => {
    renovacionesConPrimerElementoMensualidad.forEach((renovacion) => {
      if (renovacion.id === id) {
        renovacion.valor = valor;
      }
    });
  }

  const [mostrarMensualidad, setMostrarMensualidad] = useState(true);
  const handleToggle = useCallback(() => {
    setMostrarMensualidad((prev) => !prev);
  }, []);

  const [tickets, setTickets] = useTickets(
    mostrarMensualidad ? data.ticketsMensualidad : data.ticketsRenovaciones
  );
  const printingTicketRef = React.useRef(false);
  const anularTicketRef = React.useRef(false);
  const [ticketActionError, setTicketActionError] = useState('');

  const [ticketSeleccionedToNull, setTicketSeleccionedToNull] = useState(null);
  const handleAnularTicket = useCallback(async () => {
    if (anularTicketRef.current) return;
    anularTicketRef.current = true;
    setTicketActionError('');
    try {
      const tipo = mostrarMensualidad ? 'mensualidad' : 'renovacion';
      const response = await annulMonthlyTicket({
        apiUrl: API_URL,
        ticketType: tipo,
        ticketId: ticketSeleccionedToNull.id,
        httpClient: axios,
        getRequestConfig: authenticatedSessionRequestConfig,
      });
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketSeleccionedToNull.id
            ? { ...t, ...response.data, anulado: true }
            : t
        )
      );
      const ticketsKey = mostrarMensualidad ? 'ticketsMensualidad' : 'ticketsRenovaciones';
      setData((prev) => ({
        ...prev,
        [ticketsKey]: prev[ticketsKey].map((ticket) => (
          ticket.id === ticketSeleccionedToNull.id
            ? { ...ticket, ...response.data, anulado: true }
            : ticket
        )),
      }));
      if (handleUpdateData) await handleUpdateData();
    } catch (error) {
      console.error('Error al anular el ticket:', error);
      setTicketActionError('No fue posible actualizar el ticket. Actualice los datos e intente nuevamente.');
    } finally {
      anularTicketRef.current = false;
    }
  }, [handleUpdateData, mostrarMensualidad, setTickets, ticketSeleccionedToNull]);

  const handleOpenPDF = async (ticketId) => {
    if (printingTicketRef.current) return;
    const pdfWindow = openPdfPopup();
    if (!pdfWindow) return;
    printingTicketRef.current = true;
    try {
      const responseNuevoPDF = await axios.get(`${API_URL}/${mostrarMensualidad ? 'tickets-mensualidad' : 'tickets-renovacion'}/${ticketId}/`, { responseType: 'blob' });

      const blob = new Blob([responseNuevoPDF.data], { type: 'application/pdf' });
      openPdfBlob(blob, pdfWindow);
    } catch (error) {
      console.error('Error al abrir el PDF del ticket:', error);
      closePdfPopup(pdfWindow);
    } finally {
      printingTicketRef.current = false;
    }
  }

  return (
    <>
      {areUserAdmin && <Dialog
        open={openAlertDelete}
        keepMounted
        onClose={() => {setOpenAlertDelete(false)}}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>{"Espera un Segundo!"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            Estás apunto de eliminar este Vehiculo del Registro de Vehiculos de este parqueadero
            ¿Estás seguro de que quieras hacer esto?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {setOpenAlertDelete(false)}} color='error' variant="outlined">No</Button>
          <Button onClick={() => {setOpenAlertDelete(false); handleDelete(data.id, valor, data.fecha_creacion.split(" ")[0], data.movimiento, data.renovaciones.map(r => r.movimiento)); }} color='primary' variant="contained">¡Sí!</Button>
        </DialogActions>
      </Dialog>}

      { estaAtrasadoYNoSoyAdmin && <TableRow
        ref={rowRef}
        aria-current={highlighted ? 'true' : undefined}
        data-highlight-source={highlighted ? highlightSource : undefined}
        sx={{
          '& > *': { backgroundColor: colorFondo },
          ...getPersistentRowHighlightSx(theme, highlighted, searchHighlighted),
        }}
      >
        <TableCell component="th" scope="row" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black' }}> {data.id} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}>{data.historial ? `${data.historial.tipo_vehiculo} ${data.historial.color}: ${data.historial.placa}`: 'Vehículo no disponible'} </TableCell>
        <TableCell
          align="left"
          sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black' }}
        >
          {data.cliente
            ? `${data.cliente.nombre} ${data.cliente.apellidos} DOC: ${data.cliente.documento} CEL: ${data.cliente.celular}`
            : 'Cliente no disponible'}
      </TableCell>

        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {fechaInicio} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {fechaFinal} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {formatNumber(data.valorTicketModificado ? data.valorTicketModificado : valor)} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {faltanTexto} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {formatMonthlyTicketObservations(observaciones)} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {estado} </TableCell>
        <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderRight: areUserAdmin ? null : '2px solid #ddd', borderBottom: '2px solid #ddd', color: 'black'}}> {data.usuario?.nombre ?? '—'} </TableCell>
        <TableCell sx={{ borderLeft: '2px solid #ddd', borderRight: '2px solid #ddd', borderBottom: '2px solid #ddd' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>

              <IconButton
                size="large"
                sx={{ color: '#grey', border: '2px grey', paddingRight: 1 }}
                onClick={() => setOpenTicketsHistorial(!openTicketsHistorial)}
              >
                <ConfirmationNumberIcon sx={{ fontSize: '33px' }}/>
              </IconButton>
              {!data.cancelado && <>
                <IconButton
                  size="large"
                  onClick={() => { handleOpenDialogPrint(true) }} // Se ejecuta la eliminación
                  sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
                >
                  <PrintIcon sx={{ fontSize:'33px' }}/>
                </IconButton>

                <IconButton
                  size="large"
                   onClick={() => { setOpenCancelDialog(true) }} // Se ejecuta la eliminación
                  sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
                >
                  <CancelIcon sx={{ fontSize:'33px' }}/>
                </IconButton>
                
                <IconButton
                  size="large"
                   onClick={() => { setOpenDialogRenovar(true) }} // Se ejecuta la eliminación
                  sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
                >
                  <EventRepeatIcon sx={{ fontSize:'33px' }}/>
                </IconButton>

                {areUserAdmin && <IconButton
                  size="large"
                  onClick={() => {handleOpenModifyDialog()}}
                  sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
                >
                  <EditIcon sx={{ fontSize: '33px' }}/>
                </IconButton>}
              </>}
              {areUserAdmin && <IconButton
                size="large"
                onClick={() => { setOpenAlertDelete(true) }} // Se ejecuta la eliminación
                sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
              >
                <Delete sx={{ fontSize:'33px' }}/>
              </IconButton> }
              <IconButton
                  size="large"
                   onClick={() => { setOpenHistorialDialog(!openHistorialDialog) }} // Se ejecuta la eliminación
                  sx={{ color: '#grey', paddingRight: 1, border: '2px grey' }}
                >
                  <ListAltIcon sx={{ fontSize:'33px' }}/>
              </IconButton>

          </Box>
        </TableCell>
      </TableRow>
      }
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={openHistorialDialog} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography sx={{ fontWeight: 'bold' }}>
                Renovaciones de esta Mensualidad: {data.renovaciones.length}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha de Inicio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Final</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuario que Renovó</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renovacionesConPrimerElementoMensualidad.map((renovacion) => (
                    <TableRow key={renovacion.id}>
                      <TableCell>{renovacion.valorTicketModificado ? renovacion.valorTicketModificado : renovacion.valor}</TableCell>
                      <TableCell>{renovacion.fecha_entrada}</TableCell>
                      <TableCell>{renovacion.fecha_salida}</TableCell>
                      <TableCell>{renovacion.usuario}</TableCell>
                      <TableCell>{formatMonthlyTicketObservations(renovacion.observaciones)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      

      <Dialog
        open={openDialogPrint}
        onClose={handleCloseDialogPrint}
      >
        <DialogTitle>Seleccionar tipo de impresión</DialogTitle>
        <DialogContent>
          <DialogContentText paddingTop={0}>
            ¿Qué ticket deseas imprimir?
          </DialogContentText>
          {!data.renovado && (
            <Typography variant="caption" color="text.secondary">
              La mensualidad aún no tiene renovaciones.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setOpenTicketEntradaForm(true);
              handleCloseDialogPrint();
            }}
          >
            Ticket de Entrada
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!data.renovado}
            onClick={() => {
              setOpenTicketRenovacionForm(true);
              handleCloseDialogPrint();
            }}
          >
            Ticket de Renovación
          </Button>
        </DialogActions>
      </Dialog>

      <TicketEntradaForm openTicketEntradaForm={openTicketEntradaForm} setOpenTicketEntradaForm={setOpenTicketEntradaForm} data={data} areUserAdmin={areUserAdmin} handleUpdateData={handleUpdateData} actualizarValorDeUnaRenovacion={actualizarValorDeUnaRenovacion}/>
      <TicketRenovacionFormOptimizado openTicketRenovacionForm={openTicketRenovacionForm} setOpenTicketRenovacionForm={setOpenTicketRenovacionForm} data={data} areUserAdmin={areUserAdmin} handleUpdateData={handleUpdateData} actualizarValorDeUnaRenovacion={actualizarValorDeUnaRenovacion}/>

      { areUserAdmin && <DialogModifyMensualidad openDialog={openModifyDialog} handleCloseDialog={handleCloseModifyDialog} data={data} handleUpdateData={handleUpdateData}/> }
      { areUserAdmin && <DialogCancelMensualidad data={data} handleUpdateData={handleUpdateData}  handleCloseCancelDialog={handleCloseCancelDialog} openCancelDialog={openCancelDialog}/> }
      { areUserAdmin && <DialogRenovarModify openDialog={openDialogRenovar} handleCloseDialog={handleCloseRenovarDialog} data={data} handleUpdateData={handleUpdateData}/> }

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={14}>
          <Collapse in={openTicketsHistorial} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              {ticketActionError && (
                <Typography color="error" sx={{ mb: 1 }}>
                  {ticketActionError}
                </Typography>
              )}
              <Typography sx={{ fontWeight: 'bold' }}>
                Tickets de {mostrarMensualidad ? 'la Mensualidad' : 'las Renovaciones'}: {mostrarMensualidad ? data.ticketsMensualidad.length : data.ticketsRenovaciones.length}
                <Button onClick={handleToggle}>
                  {mostrarMensualidad ? 'Ver las Renovaciones' : 'Ver la Mensualidad'}
                </Button>
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Placa</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Inicio</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha Fin</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Observaciones</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Fecha-Hora de Creación</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(tickets).map((ticket) => (
                    <TableRow key={ticket.id}>

                      <TableCell>
                        {ticket.id}
                        {ticket.ticket_anterior ? (
                          <Typography variant="caption" display="block">
                            Reemplaza al ticket #{ticket.ticket_anterior}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{ticket.placa_texto ?? '—'}</TableCell>
                      <TableCell>{ticket.fecha_inicio}</TableCell>
                      <TableCell>{ticket.fecha_fin}</TableCell>
                      <TableCell>{ticket.nombre_usuario}</TableCell>
                      <TableCell>{ticket.nombre_cliente}</TableCell>
                      <TableCell>{formatMonthlyTicketObservations(ticket.observaciones)}</TableCell>
                      <TableCell>{ticket.valor}</TableCell>
                      <TableCell>{ticket?.fecha_hora_creacion ? ticket.fecha_hora_creacion.split(' ')[0] : '—'} | {ticket?.fecha_hora_creacion ? ticket.fecha_hora_creacion.split(' ')[1] : '—'}</TableCell>
                      <TableCell>{!ticket.anulado ? ticket.estado : 'ANULADO'}</TableCell>
                      <TableCell>

                        <IconButton
                          color="error"
                          aria-label={`Anular ticket ${ticket.id}`}
                          onClick={() => {
                            setTicketSeleccionedToNull(ticket);
                            setOpenTicketAnular(true);
                          }}
                          disabled={
                            ticket.anulado
                            || (mostrarMensualidad
                              ? Number(ticket.id) !== Number(data.ticket_ingreso_canonico)
                              : !ticket.renovacion)
                          }
                        >
                          <CancelIcon />
                        </IconButton>

                        <IconButton
                          onClick={() => handleOpenPDF(ticket.id)}
                        >
                          <PrintIcon />
                        </IconButton>

                        <Dialog
                          open={openTicketAnular}
                          keepMounted
                          onClose={() => setOpenTicketAnular(false)}
                          aria-describedby="alert-dialog-slide-description"
                        >
                        <DialogTitle>{"Espera un Segundo!"}</DialogTitle>
                        <DialogContent>
                          <DialogContentText id="alert-dialog-slide-description">
                            Estás a punto de anular este ticket de {mostrarMensualidad ? 'Mensualidad' : 'Renovacion'} del Registro de Tickets de este parqueadero
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenTicketAnular(false)} color='error' variant="outlined">No</Button>
                            <Button aria-label="Confirmar anulación de ticket" onClick={() => {handleAnularTicket(); setOpenTicketAnular(false);}} color='primary' variant="contained">¡Sí!</Button>
                        </DialogActions>
                      </Dialog>

                      </TableCell>

                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

    </>
  );
});

export default RowTableMensualidad;
